/**
 * Client caller for the `threadMembershipWrite` Cloud Function (group-threading P2a).
 *
 * The membership collection `prest0n_thread_participants` is SERVER-OWNED: Firestore
 * rules deny client writes (`firestore.rules` → `allow write: if false`). So the
 * browser NEVER writes membership directly — every create / add / grant / remove
 * goes through this authorized endpoint, which re-derives the actor's account from
 * the bearer token and enforces admittance + account-boundary + creator/owner
 * authorization server-side. This module only carries the request and reports the
 * server's decision; it holds NO authorization logic of its own.
 *
 * Contract: never throws. Any failure (no auth, non-2xx, network, malformed body)
 * resolves to `{ ok: false, decision }` with a client sentinel so the UI can react
 * without a try/catch. `ok` is true ONLY when the server returned 2xx AND
 * `success === true` — a fail-closed default on anything ambiguous.
 */
import { getAuth } from 'firebase/auth';

/** The membership mutations the server accepts. Mirrors the CF `MembershipEvent`. */
export type MembershipWriteEvent = 'create' | 'add' | 'remove' | 'grant' | 'post' | 'archive'
  | 'assistant-remove' | 'assistant-add' | 'name';

/** Per-thread posting capability chosen by the adder (#21). Server defaults to contributor. */
export type ThreadPostRole = 'contributor' | 'viewer';

/**
 * One storage-backed file delivered alongside a `post`. Mirrors the fields the
 * server keeps (`GroupTurnAttachment`) and the subset of the client's own
 * `SerializedAttachment` that survives a cross-device read.
 */
export interface GroupPostAttachment {
  name: string;
  type?: string;
  size?: number;
  /** Firebase Storage download URL. Required — see messageAttachments. */
  storageUrl: string;
  storagePath?: string;
}

export interface MembershipWriteInput {
  event: MembershipWriteEvent;
  threadId: string;
  /** Target participant (required for add / remove / grant). */
  targetId?: string;
  /** Initial members to seed at create time (owner/admin only, server-enforced). */
  seedMemberIds?: string[];
  /** Thread-scoped role for the added target (#21). Omitted ⇒ server default (contributor). */
  targetRole?: ThreadPostRole;
  /**
   * The thread's DEFAULT name — the one every member starts from. Carried by
   * `create` (the creator's own title at registry-create time) and by `name`
   * (2026-09-13), which publishes it to an EXISTING thread.
   */
  threadTitle?: string;
  /** `post` only: the message body delivered to the other participants. */
  messageText?: string;
  /** `post` only: stable doc id so a retried post converges instead of duplicating. */
  messageId?: string;
  /**
   * `post` only: files shared with the message (2026-08-26).
   *
   * STORAGE-BACKED ONLY. These records are read by other people's browsers, so
   * a file has to be fetchable by URL; the server drops anything without an
   * `https` `storageUrl`. Inline base64 is deliberately never sent — it would
   * push a message doc past Firestore's 1 MiB limit and fail the delivery.
   *
   * Why this exists: the composer's summon gate used to exempt every
   * attachment send, because the delivery path carried text only and
   * suppressing such a send would have silently dropped the file. That
   * exemption is what let a message addressed to a colleague draw a Prest0n
   * reply in a mention-only thread (owner report 2026-08-26). Carrying the
   * files here removed the reason for the exemption.
   */
  messageAttachments?: GroupPostAttachment[];
  /**
   * Per-thread "@Prest0n mention required to summon" setting. Absent ⇒ the
   * server leaves the thread's current setting untouched.
   */
  requireMentionToSummon?: boolean;
  /**
   * `create` only (2026-09-08, the "+" composer): start the thread WITHOUT
   * Prest0n. Only an explicit false is sent; the server honors it only on a
   * brand-new thread. An existing thread's Prest0n rides the
   * assistant-remove / assistant-add events (manage authority).
   */
  assistantEnabled?: boolean;
}

export interface MembershipWriteOutcome {
  /** True ONLY on HTTP 2xx AND server `success === true`. Fail-closed otherwise. */
  ok: boolean;
  /** Server decision code (e.g. `created`, `added`) or a `client_*` sentinel. */
  decision: string;
  threadId: string;
  workspaceId?: string;
  event?: string;
  /** HTTP status, or 0 when the request never reached the server. */
  status: number;
}

/** Resolve the `threadMembershipWrite` endpoint (env override → prod cloudfunctions URL). */
export function threadMembershipWriteUrl(): string {
  const override = (process.env.REACT_APP_FUNCTIONS_BASE || '').trim();
  const base = override
    ? override.replace(/\/$/, '')
    : `https://us-central1-${
        process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3'
      }.cloudfunctions.net`;
  return `${base}/threadMembershipWrite`;
}

/**
 * POST a membership mutation. Fail-closed: `ok` is true only on a 2xx +
 * `success === true` body; every other path returns `ok: false` with a decision
 * code (server `decision` when present, else a `client_*` sentinel). The account is
 * resolved server-side from the token — the client never asserts its own account.
 */
export async function writeThreadMembership(
  input: MembershipWriteInput,
  signal?: AbortSignal,
): Promise<MembershipWriteOutcome> {
  const threadId = (input.threadId ?? '').trim();
  const event = input.event;
  const fail = (decision: string, status = 0): MembershipWriteOutcome => ({
    ok: false,
    decision,
    threadId,
    event,
    status,
  });

  if (!threadId) return fail('client_invalid_thread_id');

  try {
    const user = getAuth().currentUser;
    if (!user) return fail('client_no_auth');
    const token = await user.getIdToken();
    if (!token) return fail('client_no_token');

    const targetId = (input.targetId ?? '').trim();
    const seedMemberIds = Array.isArray(input.seedMemberIds)
      ? [...new Set(input.seedMemberIds.map((id) => String(id ?? '').trim()).filter(Boolean))]
      : [];
    // Only forward an explicit 'viewer'; anything else lets the server default
    // to contributor (never send a malformed role).
    const targetRole = input.targetRole === 'viewer' ? 'viewer' : undefined;
    const threadTitle = typeof input.threadTitle === 'string' ? input.threadTitle.trim().slice(0, 200) : '';

    const res = await fetch(threadMembershipWriteUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        event,
        threadId,
        ...(targetId ? { targetId } : {}),
        ...(seedMemberIds.length ? { seedMemberIds } : {}),
        ...(targetRole ? { targetRole } : {}),
        // `create` carries the creator's canonical title at registry-create
        // time; `name` (2026-09-13) publishes the thread's default name to an
        // existing one. Neither is a rename — a person's own name for a thread
        // is personal and never leaves their own index doc (PR-B, 2026-09-09).
        ...((event === 'create' || event === 'name') && threadTitle ? { threadTitle } : {}),
        // Only ever sent when explicitly set, so a title-only sync cannot flip a
        // thread's summon setting.
        ...(event === 'create' && typeof input.requireMentionToSummon === 'boolean'
          ? { requireMentionToSummon: input.requireMentionToSummon }
          : {}),
        ...(event === 'create' && input.assistantEnabled === false ? { assistantEnabled: false } : {}),
        ...(event === 'post' && typeof input.messageText === 'string'
          ? { messageText: input.messageText }
          : {}),
        ...(event === 'post' && input.messageId ? { messageId: String(input.messageId).trim() } : {}),
        // Storage-backed only, re-checked here so a caller cannot post an
        // inline base64 blob by accident (the server drops these too — this
        // just keeps the request itself small enough to succeed).
        ...(event === 'post' && Array.isArray(input.messageAttachments)
          ? (() => {
              const safe = input.messageAttachments
                .filter((a) => a && typeof a.storageUrl === 'string' && /^https:\/\//i.test(a.storageUrl))
                .slice(0, 10)
                .map((a) => ({
                  name: String(a.name || 'attachment').slice(0, 200),
                  storageUrl: a.storageUrl,
                  ...(a.type ? { type: String(a.type).slice(0, 120) } : {}),
                  ...(typeof a.size === 'number' && Number.isFinite(a.size) ? { size: a.size } : {}),
                  ...(a.storagePath ? { storagePath: String(a.storagePath).slice(0, 500) } : {}),
                }));
              return safe.length ? { messageAttachments: safe } : {};
            })()
          : {}),
      }),
      signal,
    });

    const body = (await res.json().catch(() => null)) as
      | { success?: boolean; decision?: string; threadId?: string; workspaceId?: string; event?: string }
      | null;

    return {
      ok: res.ok && body?.success === true,
      decision:
        typeof body?.decision === 'string' && body.decision
          ? body.decision
          : res.ok
          ? 'client_unknown_decision'
          : 'client_http_error',
      threadId: typeof body?.threadId === 'string' && body.threadId ? body.threadId : threadId,
      workspaceId: typeof body?.workspaceId === 'string' ? body.workspaceId : undefined,
      event: typeof body?.event === 'string' ? body.event : event,
      status: res.status,
    };
  } catch {
    // AbortError included — a cancelled request is a fail-closed non-result.
    return fail('client_network_error');
  }
}

// ── Ergonomic wrappers (each is a thin, intention-revealing call) ─────────────

/**
 * Create the membership doc for a thread (idempotent server-side: an existing
 * thread resolves to `already_exists`, still `ok: true`). Optional seed members are
 * owner/admin-gated by the server.
 */
export function createThreadMembership(
  threadId: string,
  seedMemberIds?: string[],
  signal?: AbortSignal,
  threadTitle?: string,
  requireMentionToSummon?: boolean,
  options?: { assistantEnabled?: boolean },
): Promise<MembershipWriteOutcome> {
  return writeThreadMembership(
    {
      event: 'create',
      threadId,
      seedMemberIds,
      threadTitle,
      // Omitted unless explicitly set, so a title-only sync (the canonical
      // rename path) can never silently flip a thread's summon setting.
      ...(typeof requireMentionToSummon === 'boolean' ? { requireMentionToSummon } : {}),
      // The composer's "without Prest0n" (2026-09-08): explicit false only.
      ...(options?.assistantEnabled === false ? { assistantEnabled: false } : {}),
    },
    signal,
  );
}

/**
 * Publish the thread's DEFAULT name — the one every member starts from
 * (2026-09-13).
 *
 * Server authority: the thread's creator may set or replace it; any other
 * member the thread lets manage it may set the FIRST one only, decided inside
 * the write's own transaction so two browsers finishing their labeler at the
 * same instant cannot make the name flap. A member's own name for the thread
 * (`userRenamed` on their index doc) is never touched by the fan-out.
 *
 * Use `createThreadMembership`'s title at registry-create time; use this once
 * the thread exists.
 */
export function nameThreadForEveryone(
  threadId: string,
  threadTitle: string,
  signal?: AbortSignal,
): Promise<MembershipWriteOutcome> {
  return writeThreadMembership({ event: 'name', threadId, threadTitle }, signal);
}

/**
 * Put Prest0n into / take Prest0n out of a thread (2026-09-08). Authorized
 * server-side by the ONE manage predicate (creator / owner / admin /
 * threads.manage); the registry fact it writes is what every summon gate
 * reads, so a removed Prest0n stays quiet on every lane.
 */
export function setThreadAssistantEnabled(
  threadId: string,
  enabled: boolean,
  signal?: AbortSignal,
): Promise<MembershipWriteOutcome> {
  return writeThreadMembership({ event: enabled ? 'assistant-add' : 'assistant-remove', threadId }, signal);
}

/**
 * Deliver a human message to the other participants WITHOUT summoning Prest0n
 * (W3a, owner directive 2026-08-06).
 *
 * The mention toggle is unshippable without this: a human message only reaches
 * the group store when a turn completes, so suppressing Prest0n's reply would
 * otherwise suppress the message itself and the thread would silently stop
 * delivering. `messageId` must be the caller's own stable bubble id — the server
 * writes under it, so a retry converges by merge rather than duplicating.
 *
 * Authorized server-side by the same postEligibility gate as every other write:
 * a thread 'viewer' is refused here exactly as on the normal send path.
 */
export function postGroupThreadMessage(
  threadId: string,
  messageText: string,
  messageId: string,
  signal?: AbortSignal,
  messageAttachments?: GroupPostAttachment[],
): Promise<MembershipWriteOutcome> {
  return writeThreadMembership(
    {
      event: 'post',
      threadId,
      messageText,
      messageId,
      ...(messageAttachments?.length ? { messageAttachments } : {}),
    },
    signal,
  );
}

/**
 * Set this thread's "@Prest0n mention required to summon" setting (owner
 * directive 2026-08-06). Rides the idempotent create upsert — same authority
 * checks and audit trail as the canonical title.
 */
export function setThreadSummonRequirement(
  threadId: string,
  requireMentionToSummon: boolean,
  signal?: AbortSignal,
): Promise<MembershipWriteOutcome> {
  return writeThreadMembership(
    { event: 'create', threadId, requireMentionToSummon },
    signal,
  );
}

/**
 * Add a participant to an existing thread (creator / owner / admin, server-enforced).
 * `targetRole` sets their per-thread posting capability (#21); omit for contributor.
 */
export function addThreadParticipant(
  threadId: string,
  targetId: string,
  targetRole?: ThreadPostRole,
  signal?: AbortSignal,
): Promise<MembershipWriteOutcome> {
  return writeThreadMembership({ event: 'add', threadId, targetId, targetRole }, signal);
}

/** Grant a participant full (pre-join) history visibility. */
export function grantThreadFullHistory(
  threadId: string,
  targetId: string,
  signal?: AbortSignal,
): Promise<MembershipWriteOutcome> {
  return writeThreadMembership({ event: 'grant', threadId, targetId }, signal);
}

/**
 * Archive a group thread FOR EVERYONE in it (owner directive 2026-08-26).
 *
 * The server stamps `archivedForAll` on the shared registry (permanent — the
 * membership projection hides the thread from every member's surfacing pass
 * from then on, which is what stops archived threads coming back), and fans
 * `archived: true` out to every member's own thread-index doc, so each rail
 * updates through the live index listener the member already holds. Any
 * member may call it; the server refuses outsiders.
 */
export function archiveGroupThreadForEveryone(
  threadId: string,
  signal?: AbortSignal,
): Promise<MembershipWriteOutcome> {
  return writeThreadMembership({ event: 'archive', threadId }, signal);
}

/** Remove a participant from a thread. */
export function removeThreadParticipant(
  threadId: string,
  targetId: string,
  signal?: AbortSignal,
): Promise<MembershipWriteOutcome> {
  return writeThreadMembership({ event: 'remove', threadId, targetId }, signal);
}

